import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useChangePasswordMutation } from "@/features/organizers/api";
import { ApiError, showApiError } from "@/lib/api-client";
import { useT } from "@/lib/i18n";

interface ChangePasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Giriş yapmış member'ın şifre değişikliği. Backend mevcut session'ı açık
 * tutar, diğer cihazlardaki session'ları kapatır.
 */
export function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
  const t = useT("auth.changePassword");
  return (
    <Dialog open={open} onOpenChange={onOpenChange} disableBackdropClose>
      <DialogContent>
        <DialogHeader title={t("button")} description={t("description")} />
        {/* Dialog kapanınca unmount olur → form state her açılışta sıfırlanır. */}
        <ChangePasswordForm onDone={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

function ChangePasswordForm({ onDone }: { onDone: () => void }) {
  const t = useT("auth.changePassword");
  const tCommon = useT("common");
  const change = useChangePasswordMutation();

  const schema = z
    .object({
      currentPassword: z.string().min(1, t("currentRequired")),
      newPassword: z.string().min(8, t("tooShort")),
      confirm: z.string(),
    })
    .refine((v) => v.newPassword === v.confirm, {
      path: ["confirm"],
      message: t("mismatch"),
    });

  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: "", newPassword: "", confirm: "" },
  });

  async function onSubmit(values: Values) {
    try {
      await change.mutateAsync({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success(t("success"));
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.code === "invalid_current_password") {
        form.setError("currentPassword", { message: err.detail });
        return;
      }
      if (err instanceof ApiError && err.code === "same_password") {
        form.setError("newPassword", { message: err.detail });
        return;
      }
      if (err instanceof ApiError && err.status === 429) {
        toast.error(t("tooManyRequests"));
        return;
      }
      showApiError(err, tCommon("unexpectedError"));
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="currentPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("current")}</FormLabel>
              <FormControl>
                <Input type="password" autoComplete="current-password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="newPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("new")}</FormLabel>
              <FormControl>
                <Input type="password" autoComplete="new-password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirm"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("confirm")}</FormLabel>
              <FormControl>
                <Input type="password" autoComplete="new-password" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onDone} disabled={change.isPending}>
            {tCommon("cancel")}
          </Button>
          <Button type="submit" disabled={change.isPending}>
            {change.isPending ? tCommon("saving") : t("submit")}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
